<template>
  <DatatableReportV3
    v-if="config"
    id="payment_exports_report"
    ref="datatable"
    :source="source"
    :config="config"
    :filters="filters"
    hide-global-filters
    disable-filter-param-update
    no-download
    subpage
    bulk-select
    bulk-select-across-pages
    bulk-select-keep-alive
    bulk-select-key="checkbox_row_value"
    :bulk-select-ids="selectedPaymentIds"
    :map-row-class="handleMapRowClass"
    @draw-complete="$emit('draw-complete', $event)"
    @update:bulk-select-ids="handleUpdateBulkSelectIds"
    @after-init="selectAll"
  />
</template>

<script>
import { mapActions, mapState } from "vuex";

import DatatableReportV3 from "@/Reports/DatatableReportV3.vue";
import UnbatchedExportStatus from "@/Reports/UnbatchedExportStatus.vue";
import store from "@/vuex/store";

export default {
  components: {
    DatatableReportV3
  },
  provide() {
    return {
      datatableAfterInit: this.selectAll
    };
  },
  inject: [
    "selectedPaymentIds",
    "updateSelectedPaymentIds",
    "resolvePendingPaymentRefresh"
  ],
  props: {
    source: {
      type: String,
      default: ""
    },
    filters: {
      type: Array,
      default: () => []
    },
    qboEnabled: {
      type: Boolean,
      default: false
    },
    totalRows: {
      type: Number,
      default: 0
    }
  },
  emits: ["draw-complete"],
  data() {
    return {
      config: null
    };
  },
  computed: {
    ...mapState(["tenant", "fetchingTenant"])
  },
  watch: {
    tenant(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.setConfig();
      }
    }
  },
  mounted() {
    if (!this.tenant && !this.fetchingTenant) {
      this.fetchTenant();
    }
    if (this.tenant) {
      this.setConfig();
    }
  },
  methods: {
    ...mapActions(["fetchTenant"]),
    handleMapRowClass(params) {
      if (!this.qboEnabled) {
        return "";
      }

      return params.data.export_status_value === "Failed" ? "failed" : "";
    },
    handleUpdateBulkSelectIds(ids) {
      this.updateSelectedPaymentIds(ids);
    },
    setConfig() {
      let columns = [
        {
          data: "status",
          title: "Type",
          filterName: "payment_batch_type",
          orderable: false
        }
      ];

      if (this.qboEnabled) {
        columns = [].concat(columns, [
          {
            data: "payment_export_status",
            title: "Export Status",
            orderable: false,
            searchable: false
          }
        ]);
      }

      columns = [].concat(columns, [
        { data: "transaction_date", title: "Date", searchable: false },
        { data: "invoice_date", title: "Invoice Date", searchable: false },
        { data: "invoice" },
        { data: "job" },
        { data: "location_name", title: "Location Name", orderable: false },
        {
          data: "service_address",
          title: "Service Address",
          orderable: false
        },
        { data: "customer" },
        { data: "customer_email", title: "Email" },
        { data: "customer_phone", title: "Phone" },
        { data: "address", title: "Billing Address", orderable: false },
        { data: "department", searchable: false },
        {
          data: "reconciled",
          filterName: "reconciled_status",
          searchable: false
        },
        {
          data: "receiver",
          filterName: "transaction_received_by",
          title: "Received By",
          orderable: false,
          searchable: false
        },
        { data: "transaction_type", title: "Payment Type" },
        { data: "amount", searchable: false, className: "text-right" }
      ]);

      const sortColumnName = "transaction_date";

      let sortColumn = 1;
      for (let i = 0; i < columns.length; i++) {
        if (columns[i].data === sortColumnName) {
          sortColumn = i + 1;
          break;
        }
      }

      this.config = {
        order: [[sortColumn, "desc"]],
        columns,
        vueComponents: [
          {
            rootId: "export-status-vue",
            component: UnbatchedExportStatus,
            options: {
              store
            }
          }
        ]
      };
    },
    refreshDataAndSelectAll() {
      const childDatatable = this.$refs.datatable.$refs.datatable;
      const exportReport = this;
      childDatatable.refreshDataWithCallback(function () {
        exportReport.resolvePendingPaymentRefresh();
        childDatatable.markAllSelectionsAcrossPages();
      });
    },
    selectAll() {
      const childDatatable = this.$refs.datatable.$refs.datatable;
      this.resolvePendingPaymentRefresh();
      childDatatable.markAllSelectionsAcrossPages();
    }
  }
};
</script>

<style lang="scss" scoped>
:deep(tr.failed.even:nth-child(2n)) {
  background-color: var(--sera-color-accents-red-100) !important;
}

:deep(tr.failed.odd) {
  background-color: var(--sera-color-accents-red-100) !important;
}

:deep(tr.failed.even:nth-child(2n)) {
  background-color: var(--sera-color-accents-red-100) !important;
  &.dt-selected-row {
    background-color: var(--sera-color-accents-red-200) !important;

    .status-badge {
      background-color: var(--sera-color-accents-red-300) !important;
    }
  }
}

:deep(tr.failed.odd) {
  background-color: var(--sera-color-accents-red-100) !important;
  &.dt-selected-row {
    background-color: var(--sera-color-accents-red-200) !important;

    .status-badge {
      background-color: var(--sera-color-accents-red-300) !important;
    }
  }
}
</style>
